// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

export const DEFAULT_MAX_TOUR_PLAYERS = 8;

type SelectionPlayer = {
  id: string;
  userId?: string | null;
};

type ToggleSelectionResult = {
  nextSelectedPlayerIds: string[];
  blockedByCreator: boolean;
  blockedByMax: boolean;
};

const normalizeId = (id: string | null | undefined): string | null => {
  if (!id) {
    return null;
  }

  const trimmed = id.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const dedupeIds = (ids: readonly string[]): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  ids.forEach((id) => {
    const normalized = normalizeId(id);
    if (!normalized || seen.has(normalized)) {
      return;
    }

    seen.add(normalized);
    result.push(normalized);
  });
  return result;
};

const trimToMaxKeepingCreator = (
  ids: readonly string[],
  creatorPlayerId: string | null,
  maxPlayers: number
): string[] => {
  if (maxPlayers <= 0) {
    return [];
  }

  if (ids.length <= maxPlayers) {
    return [...ids];
  }

  if (!creatorPlayerId || !ids.includes(creatorPlayerId)) {
    return ids.slice(0, maxPlayers);
  }

  const withoutCreator = ids.filter((id) => id !== creatorPlayerId);
  return [creatorPlayerId, ...withoutCreator.slice(0, Math.max(0, maxPlayers - 1))];
};

export function getCreatorPlayerId(
  players: readonly SelectionPlayer[],
  currentUserId?: string | null,
  fallbackCreatorPlayerId?: string | null
): string | null {
  const normalizedUserId = normalizeId(currentUserId);
  if (normalizedUserId) {
    const directMatch = players.find((player) => normalizeId(player.userId) === normalizedUserId);
    if (directMatch) {
      return directMatch.id;
    }
  }

  return normalizeId(fallbackCreatorPlayerId);
}

export function buildTourInitiatePlayerIds(
  selectedPlayerIds: readonly string[],
  creatorPlayerId?: string | null,
  maxPlayers: number = DEFAULT_MAX_TOUR_PLAYERS
): string[] {
  const normalizedCreatorPlayerId = normalizeId(creatorPlayerId);
  const deduped = dedupeIds(selectedPlayerIds);
  let result = deduped;

  if (normalizedCreatorPlayerId && !result.includes(normalizedCreatorPlayerId)) {
    if (result.length >= maxPlayers && maxPlayers > 0) {
      result = result.slice(0, maxPlayers - 1);
    }
    result = [...result, normalizedCreatorPlayerId];
  }

  return trimToMaxKeepingCreator(result, normalizedCreatorPlayerId, maxPlayers);
}

export function normalizeTourCreateSelection(
  selectedPlayerIds: readonly string[],
  availablePlayers: readonly SelectionPlayer[],
  creatorPlayerId?: string | null,
  maxPlayers: number = DEFAULT_MAX_TOUR_PLAYERS
): string[] {
  const availablePlayerIds = new Set(
    availablePlayers
      .map((player) => normalizeId(player.id))
      .filter((id): id is string => Boolean(id))
  );

  const filtered = selectedPlayerIds.filter((id) => availablePlayerIds.has(id));
  return buildTourInitiatePlayerIds(filtered, creatorPlayerId, maxPlayers);
}

export function toggleTourMemberSelection(
  selectedPlayerIds: readonly string[],
  playerId: string,
  creatorPlayerId?: string | null,
  maxPlayers: number = DEFAULT_MAX_TOUR_PLAYERS
): ToggleSelectionResult {
  const normalizedPlayerId = normalizeId(playerId);
  const normalizedCreatorPlayerId = normalizeId(creatorPlayerId);
  if (!normalizedPlayerId) {
    return {
      nextSelectedPlayerIds: [...selectedPlayerIds],
      blockedByCreator: false,
      blockedByMax: false,
    };
  }

  const current = dedupeIds(selectedPlayerIds);
  if (current.includes(normalizedPlayerId)) {
    if (normalizedCreatorPlayerId && normalizedPlayerId === normalizedCreatorPlayerId) {
      return {
        nextSelectedPlayerIds: current,
        blockedByCreator: true,
        blockedByMax: false,
      };
    }

    return {
      nextSelectedPlayerIds: current.filter((id) => id !== normalizedPlayerId),
      blockedByCreator: false,
      blockedByMax: false,
    };
  }

  if (current.length >= maxPlayers) {
    return {
      nextSelectedPlayerIds: current,
      blockedByCreator: false,
      blockedByMax: true,
    };
  }

  return {
    nextSelectedPlayerIds: buildTourInitiatePlayerIds([...current, normalizedPlayerId], normalizedCreatorPlayerId, maxPlayers),
    blockedByCreator: false,
    blockedByMax: false,
  };
}

export function areSameIdLists(left: readonly string[], right: readonly string[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
}
